import { useCallback, useMemo } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { TriageGatesResponse, TriageStage } from '@/types/report-request';
import { toError } from '@/utils/errors';
import { usePoll } from './usePoll';

/** Fast poll while identification is running server-side. */
const AI_POLL_INTERVAL_MS = 3000;
/** Slow poll while the Partners step is active — assignment edits happen
 * through the NP provider's own hooks, so the strip re-evaluates on a
 * timer rather than wiring a callback through every mutation. */
const PARTNERS_POLL_INTERVAL_MS = 5000;

function hasAiWorking(gates: TriageGatesResponse | undefined): boolean {
  return (gates?.blockers ?? []).some((b) => b.code === 'ai_working');
}

/** Background file digestion in flight (or failures awaiting a rail
 * retry) — poll so the "N file(s) still being read" count ticks down
 * as per-file commits land, instead of freezing at the initial N. */
function hasDigesting(gates: TriageGatesResponse | undefined): boolean {
  return (gates?.blockers ?? []).some(
    (b) => b.code === 'attachments_digesting' || b.code === 'attachment_digest_failed'
  );
}

/**
 * Gates for the triage workspace's strip — read-only exit-condition
 * evaluation from `GET /report-requests/{id}/gates`.
 *
 * Refetches whenever `stageKey` or `refreshKey` changes. The page passes
 * the whole detail object as `refreshKey`: every mutation that reloads
 * the detail (candidate selection, subject edits, pricing, …) gets a
 * fresh object identity, so the strip's blockers never go stale after a
 * same-stage mutation. Polls at 3s while an `ai_working` blocker is
 * present, and at 5s while digesting or the request sits at `np_selected`.
 */
export function useTriageGates(
  reportRequestId: string | undefined,
  stageKey: TriageStage | null | undefined,
  refreshKey?: unknown
) {
  const poll = usePoll<TriageGatesResponse>({
    enabled: reportRequestId !== undefined,
    refetchDeps: [reportRequestId, stageKey, refreshKey],
    // Cadence derives from the last response AND the stage — `usePoll`
    // re-reads this after every fetch and render, so a stage change
    // re-arms it without a restart.
    interval: (gates) => {
      if (hasAiWorking(gates)) {
        return AI_POLL_INTERVAL_MS;
      }
      return hasDigesting(gates) || stageKey === 'np_selected' ? PARTNERS_POLL_INTERVAL_MS : null;
    },
    queryFn: async ({ signal }) => {
      if (!reportRequestId) {
        throw new Error('reportRequestId is required');
      }
      return vnextApiClient.getTriageGates(reportRequestId, { signal });
    },
    onError: (err) => Sentry.captureException(toError(err, 'Failed to load gates')),
  });

  const gates = poll.data ?? null;
  const aiWorking = useMemo(() => hasAiWorking(gates ?? undefined), [gates]);
  const blocking = useMemo(
    () => (gates?.blockers ?? []).filter((b) => b.severity === 'blocking'),
    [gates]
  );

  const { refetch: pollRefetch } = poll;
  const refetch = useCallback(
    () => (reportRequestId ? pollRefetch() : Promise.resolve()),
    [reportRequestId, pollRefetch]
  );

  return {
    gates,
    blocking,
    aiWorking,
    error: poll.error === undefined ? null : toError(poll.error, 'Failed to load gates'),
    refetch,
  };
}
